import { type FC, type JSX, memo } from 'react';
import classnames from 'classnames';
import type { IconSize } from './icon-constants';
import { ICON_SIZE } from './icon-constants';
import type { IconClassName } from './index';

//  Helper for testing font-awesome icons
const FA_PREFIX = 'fa-';
const isFontAwesome = (icon: string): boolean => icon.startsWith(FA_PREFIX);

const fontAwesomeClasses = ({ hasSpin, size }: { hasSpin: boolean; size?: IconSize }) => ({
  'fa-spin': hasSpin,
  'fa-sm': size === ICON_SIZE.SM,
  'fa-lg': size === ICON_SIZE.LG,
  'fa-2x': size === ICON_SIZE.XL,
  'fa-3x': size === ICON_SIZE.XXL,
});

export type IconProps = {
  /**
   * The icon which you wish to use
   */
  icon: IconClassName;
  /**
   * If the fa-spin class should be added to the icon, which causes the icon to animate as a spinner
   */
  hasSpin?: boolean;
  /**
   * Currently adds the fa-lg class to the icon when it equals to 'lg'. Other sizes and `fossa-` icon support may be added.
   */
  size?: IconSize;
  /**
   * The data-testid attribute for testing purposes
   */
  testId?: string;
};

/**
 * Standardized Icon component. Supports both `fa-` and `fossa-` icons.
 * Read more about FOSSA's icons here: https://www.notion.so/fossa/Iconography-6082eb23749b4db0a25fb9edd7bf49aa
 *
 * @example
 * import React from 'react';
 * import Icon, { icons } from '../generic/Icon';
 *
 * const MyComponent = () => (
 *  <Icon icon={icons.infoCircle} />
 * );
 *
 * export default MyComponent;
 */
const Icon: FC<IconProps> = ({ icon, hasSpin = false, size, testId }) =>
  (
    <i
      className={classnames(icon, isFontAwesome(icon) && fontAwesomeClasses({ hasSpin, size }))}
      aria-hidden="true"
      data-testid={testId}
    />
  ) as JSX.Element;

export default memo(Icon);
